/**
 * Service Level / Fill Rate KPI (5B)
 * Shows per-SKU x Store service level calculated as:
 *   Service Level % = Actual Sales / (Actual Sales + Lost Sales) x 100
 * Where Lost Sales = Days OOS x Avg Daily Sales
 *
 * Color coding: Green >95%, Yellow 80-95%, Red <80%
 */
import React, { useState, useMemo, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Grid, TextField, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, FormControl, InputLabel, Select, MenuItem,
  TableSortLabel,
} from '@mui/material';
import {
  Refresh,
} from '@mui/icons-material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ReferenceLine,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import ExportMenu from '../Common/ExportMenu';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

const PIE_COLORS = ['#722F37', '#B76E79', '#C9A96E', '#8B7355', '#8B6F8B', '#DEB5A0', '#8B2438', '#16A34A'];

const chartTooltipStyle = {
  background: '#FFFFFF',
  border: '1px solid #E5E5E0',
  borderRadius: '6px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
  fontSize: '12px',
  padding: '8px 12px',
};

type SortField = 'sku' | 'store_name' | 'avg_daily_sales' | 'days_oos' | 'actual_sales_qty' | 'lost_sales_qty' | 'lost_sales_value' | 'service_level_pct';
type SortDir = 'asc' | 'desc';

const getServiceLevelColor = (pct: number): string => {
  if (pct >= 95) return 'var(--color-success)';
  if (pct >= 80) return 'var(--color-warning)';
  return 'var(--color-danger)';
};

const getServiceLevelBg = (pct: number): string => {
  if (pct >= 95) return 'var(--color-success-bg)';
  if (pct >= 80) return 'var(--color-warning-bg)';
  return 'var(--color-danger-bg)';
};

const ServiceLevelKPI: React.FC = () => {
  const { fmtCurrency } = useCurrencyFormat();
  const [startDate, setStartDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 12)).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [data, setData] = useState<any>(null);
  const [monthlyData, setMonthlyData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('service_level_pct');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const { data: stores } = useQuery({
    queryKey: ['stores-sl'],
    queryFn: async () => { const r = await api.getStores(); return r.data; },
  });

  const { data: brands } = useQuery({
    queryKey: ['filter-brands-sl'],
    queryFn: async () => { const r = await api.getBrands(); return r.data; },
  });

  const loadServiceLevel = async () => {
    setLoading(true);
    setError(null);
    const params = {
      start_date: startDate,
      end_date: endDate,
      store_id: selectedStore || undefined,
      brand: selectedBrand || undefined,
    };
    try {
      const [detailRes, monthlyRes] = await Promise.all([
        api.getServiceLevelDetail(params),
        api.getServiceLevelMonthly(params),
      ]);
      setData(detailRes.data);
      setMonthlyData(monthlyRes.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load service level data');
    } finally {
      setLoading(false);
    }
  };

  // Root cause pie chart data
  const rootCauseData = useMemo(() => {
    if (!monthlyData?.root_cause_summary) return [];
    return monthlyData.root_cause_summary.map((rc: any) => ({
      name: rc.root_cause,
      value: rc.count,
      lost_revenue: rc.lost_revenue,
      lost_units: rc.lost_units,
    }));
  }, [monthlyData]);

  // Monthly evolution chart data
  const monthlyChartData = useMemo(() => {
    if (!monthlyData?.months) return [];
    return monthlyData.months;
  }, [monthlyData]);

  // ---- Top 10 SKUs by lost sales, per category ----
  const CATEGORY_TABS = [
    { key: 'overall', label: 'Overall', filter: null },
    { key: 'fragrance', label: 'Fragrance', filter: 'Fragrance' },
    { key: 'makeup', label: 'Make Up', filter: 'Make-up' },
    { key: 'skincare', label: 'Skincare', filter: 'Skincare' },
  ];

  const top10ByCategory = useMemo(() => {
    if (!data?.detail) return {};

    // Aggregate lost sales per product (across all stores)
    const productMap: Record<string, {
      product_id: string; sku: string; product_name: string;
      brand: string; category: string;
      lost_sales_qty: number; lost_sales_value: number;
    }> = {};
    for (const row of data.detail) {
      const pid = row.product_id;
      if (!productMap[pid]) {
        productMap[pid] = {
          product_id: pid,
          sku: row.sku,
          product_name: row.product_name,
          brand: row.brand || '',
          category: row.category || '',
          lost_sales_qty: 0,
          lost_sales_value: 0,
        };
      }
      productMap[pid].lost_sales_qty += row.lost_sales_qty || 0;
      productMap[pid].lost_sales_value += row.lost_sales_value || 0;
    }

    const allProducts = Object.values(productMap);

    const result: Record<string, {
      top10: typeof allProducts;
      sumQty: number; sumValue: number;
      totalQty: number; totalValue: number;
      pctQty: number; pctValue: number;
    }> = {};

    for (const tab of CATEGORY_TABS) {
      const filtered = tab.filter
        ? allProducts.filter((p) => p.category === tab.filter)
        : allProducts;

      const totalQty = filtered.reduce((s, p) => s + p.lost_sales_qty, 0);
      const totalValue = filtered.reduce((s, p) => s + p.lost_sales_value, 0);

      const sorted = [...filtered].sort((a, b) => b.lost_sales_value - a.lost_sales_value);
      const top10 = sorted.slice(0, 10);
      const sumQty = top10.reduce((s, p) => s + p.lost_sales_qty, 0);
      const sumValue = top10.reduce((s, p) => s + p.lost_sales_value, 0);

      result[tab.key] = {
        top10,
        sumQty,
        sumValue,
        totalQty,
        totalValue,
        pctQty: totalQty > 0 ? Math.round((sumQty / totalQty) * 1000) / 10 : 0,
        pctValue: totalValue > 0 ? Math.round((sumValue / totalValue) * 1000) / 10 : 0,
      };
    }

    return result;
  }, [data]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'service_level_pct' ? 'asc' : 'desc');
    }
  };

  const sortedData = useMemo(() => {
    if (!data?.detail) return [];
    return [...data.detail].sort((a: any, b: any) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [data, sortField, sortDir]);

  const exportRows = useMemo(
    () =>
      sortedData.map((row: any) => ({
        SKU: row.sku,
        Product: row.product_name,
        Brand: row.brand || '',
        Store: row.store_name,
        City: row.city || '',
        'Avg Daily Sales': Math.round(row.avg_daily_sales ?? 0),
        'Days in Period': row.days_in_period,
        'Days OOS': row.days_oos,
        'Actual Sales (qty)': row.actual_sales_qty,
        'Lost Sales (qty)': row.lost_sales_qty,
        'Lost Sales (value)': row.lost_sales_value?.toFixed(2),
        'Service Level %': row.service_level_pct?.toFixed(1),
      })),
    [sortedData],
  );

  const SortHeader = ({ field, label, align, width }: { field: SortField; label: string; align?: 'right' | 'left'; width?: number }) => (
    <TableCell align={align} sx={{ fontSize: '11px', fontWeight: 700, py: '6px', px: '8px', whiteSpace: 'nowrap', width: width || 'auto' }}>
      <TableSortLabel active={sortField === field} direction={sortField === field ? sortDir : 'asc'} onClick={() => handleSort(field)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-text-primary)', fontFamily: 'var(--font-heading)' }}>
            Service Level / Fill Rate
          </Typography>
          {data && (
            <ExportMenu
              data={exportRows}
              filename={`Service_Level_${startDate}_${endDate}`}
              title={`Service Level KPI ${startDate} → ${endDate}`}
              sheetName="Service Level"
              chartRef={chartContainerRef}
            />
          )}
        </Box>
        <Typography sx={{ fontSize: 'var(--text-base)', color: 'var(--color-text-muted)', mb: 2 }}>
          Service Level = Actual Sales / (Actual Sales + Lost Sales) x 100.
          Lost sales are estimated from days out of stock x average daily sales.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth type="date" label="Start Date" value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth type="date" label="End Date" value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Store</InputLabel>
              <Select value={selectedStore} label="Store" onChange={(e) => setSelectedStore(e.target.value)}>
                <MenuItem value="">All Stores</MenuItem>
                {stores?.map((s: any) => <MenuItem key={s.store_id} value={s.store_id}>{s.store_name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Brand</InputLabel>
              <Select value={selectedBrand} label="Brand" onChange={(e) => setSelectedBrand(e.target.value)}>
                <MenuItem value="">All Brands</MenuItem>
                {brands?.map((b: string) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained" startIcon={<Refresh />} onClick={loadServiceLevel}
              fullWidth sx={{ height: '56px' }} disabled={loading}
            >
              {loading ? 'Loading...' : 'Load'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      )}

      {data?.summary && (
        <>
          {/* Summary KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', background: 'var(--color-surface)' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5 }}>
                  Overall Service Level
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-3xl)', fontWeight: 700, color: getServiceLevelColor(data.summary.overall_service_level_pct), fontVariantNumeric: 'tabular-nums' }}>
                  {data.summary.overall_service_level_pct.toFixed(1)}%
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', background: 'var(--color-surface)' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5 }}>
                  Total Lost Sales (qty)
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-3xl)', fontWeight: 700, color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {data.summary.total_lost_sales_qty.toLocaleString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', background: 'var(--color-surface)' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5 }}>
                  Total Lost Sales (value)
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-3xl)', fontWeight: 700, color: 'var(--color-danger)', fontVariantNumeric: 'tabular-nums' }}>
                  {fmtCurrency(data.summary.total_lost_sales_value)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '16px 20px', background: 'var(--color-surface)' }}>
                <Typography sx={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--color-text-muted)', fontWeight: 600, mb: 0.5 }}>
                  SKU x Store Combinations
                </Typography>
                <Typography sx={{ fontSize: 'var(--text-3xl)', fontWeight: 700, color: 'var(--color-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {data.summary.total_sku_store_combinations}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Charts: Root Cause Pie + Monthly Evolution */}
          <Grid container spacing={3} sx={{ mb: 3 }} ref={chartContainerRef as any}>
            {/* Root Cause Pie Chart */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', height: '100%' }}>
                <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>
                  Stockout Root Cause Analysis
                </Typography>
                <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 1.5 }}>
                  Distribution of stockout events by root cause
                </Typography>
                {rootCauseData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={rootCauseData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={85}
                          innerRadius={40}
                          paddingAngle={2}
                          label={false}
                        >
                          {rootCauseData.map((_: any, idx: number) => (
                            <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <RTooltip
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const d = payload[0].payload;
                            return (
                              <Paper sx={{ p: 1.5, border: '1px solid var(--color-border)', fontSize: '11px' }}>
                                <Typography sx={{ fontWeight: 600, fontSize: '12px', mb: 0.5 }}>{d.name}</Typography>
                                <Typography sx={{ fontSize: '11px' }}>Events: <strong>{d.value}</strong></Typography>
                                <Typography sx={{ fontSize: '11px' }}>Lost Units: <strong>{d.lost_units?.toLocaleString()}</strong></Typography>
                                <Typography sx={{ fontSize: '11px' }}>Lost Revenue: <strong>{fmtCurrency(d.lost_revenue)}</strong></Typography>
                              </Paper>
                            );
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Legend with values below the chart */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mt: 0.5 }}>
                      {rootCauseData.map((d: any, idx: number) => (
                        <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: PIE_COLORS[idx % PIE_COLORS.length], flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '11px', color: 'var(--color-text-primary)' }}>
                            {d.name}: <strong>{d.value}</strong>
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                ) : (
                  <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>No root cause data available</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Monthly Service Level Evolution */}
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: '20px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', height: '100%' }}>
                <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 0.5 }}>
                  Service Level Evolution
                </Typography>
                <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)', mb: 1.5 }}>
                  <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: '#C9A96E', borderRadius: '2px', mr: 0.5, verticalAlign: 'middle' }} /> Lost Sales Qty
                  {' · '}
                  <Box component="span" sx={{ display: 'inline-block', width: 10, height: 3, bgcolor: '#722F37', borderRadius: '1px', mr: 0.5, verticalAlign: 'middle' }} /> Service Level %
                </Typography>
                {monthlyChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={monthlyChartData} margin={{ left: 0, right: 8, top: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                      <XAxis
                        dataKey="month_label"
                        tick={{ fontSize: 10, fill: '#8A8A85' }}
                        tickLine={false}
                        axisLine={{ stroke: '#E5E5E0' }}
                      />
                      <YAxis
                        yAxisId="qty"
                        orientation="left"
                        tick={{ fontSize: 10, fill: '#8A8A85' }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : `${v}`}
                        label={{ value: 'Lost Qty', angle: -90, position: 'insideLeft', offset: -5, style: { fontSize: 10, fill: '#8A8A85' } }}
                      />
                      <YAxis
                        yAxisId="pct"
                        orientation="right"
                        domain={[0, 100]}
                        tick={{ fontSize: 10, fill: '#8A8A85' }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v: number) => `${v}%`}
                        label={{ value: 'Service %', angle: 90, position: 'insideRight', offset: -5, style: { fontSize: 10, fill: '#8A8A85' } }}
                      />
                      <RTooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0]?.payload;
                          return (
                            <Paper sx={{ p: 1.5, border: '1px solid var(--color-border)', fontSize: '11px' }}>
                              <Typography sx={{ fontWeight: 600, fontSize: '12px', mb: 0.5 }}>{label}</Typography>
                              <Typography sx={{ fontSize: '11px' }}>Service Level: <strong style={{ color: getServiceLevelColor(d.service_level_pct) }}>{d.service_level_pct}%</strong></Typography>
                              <Typography sx={{ fontSize: '11px' }}>Actual Sales: <strong>{d.actual_sales_qty?.toLocaleString()}</strong></Typography>
                              <Typography sx={{ fontSize: '11px' }}>Lost Sales: <strong style={{ color: 'var(--color-danger)' }}>{d.lost_sales_qty?.toLocaleString()}</strong></Typography>
                              <Typography sx={{ fontSize: '11px' }}>SKU×Store: {d.sku_store_count}</Typography>
                            </Paper>
                          );
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                      <ReferenceLine yAxisId="pct" y={95} stroke="#16A34A" strokeDasharray="4 4" label={{ value: '95% target', position: 'insideTopRight', fontSize: 10, fill: '#16A34A' }} />
                      <Bar yAxisId="qty" dataKey="lost_sales_qty" name="Lost Sales Qty" fill="#C9A96E" radius={[3, 3, 0, 0]} barSize={24} opacity={0.8} />
                      <Line yAxisId="pct" type="monotone" dataKey="service_level_pct" name="Service Level %" stroke="#722F37" strokeWidth={2.5} dot={{ r: 4, fill: '#722F37', stroke: '#fff', strokeWidth: 2 }} activeDot={{ r: 6 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>No monthly data available</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Top 10 SKUs Most Impactful in Lost Sales — by Category */}
          {data?.detail?.length > 0 && Object.keys(top10ByCategory).length > 0 && (
            <>
              <Typography sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, mb: 1.5 }}>
                Top 10 SKUs — Highest Lost Sales Impact
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {CATEGORY_TABS.map((tab) => {
                  const cat = top10ByCategory[tab.key];
                  if (!cat) return null;
                  return (
                    <Grid item xs={12} md={3} key={tab.key}>
                      <Paper sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Card header */}
                        <Box sx={{ px: '12px', py: '8px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>
                            {tab.label}
                          </Typography>
                        </Box>

                        {/* Mini table */}
                        <TableContainer sx={{ flex: 1 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontSize: '9px', fontWeight: 700, py: '4px', px: '6px', whiteSpace: 'nowrap' }}>#</TableCell>
                                <TableCell sx={{ fontSize: '9px', fontWeight: 700, py: '4px', px: '6px' }}>Product</TableCell>
                                <TableCell sx={{ fontSize: '9px', fontWeight: 700, py: '4px', px: '6px' }}>Brand</TableCell>
                                <TableCell sx={{ fontSize: '9px', fontWeight: 700, py: '4px', px: '6px' }}>Category</TableCell>
                                <TableCell align="right" sx={{ fontSize: '9px', fontWeight: 700, py: '4px', px: '6px' }}>Lost Qty</TableCell>
                                <TableCell align="right" sx={{ fontSize: '9px', fontWeight: 700, py: '4px', px: '6px' }}>Lost Value</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {cat.top10.map((p, idx) => (
                                <TableRow key={p.product_id} hover sx={{ '& .MuiTableCell-body': { fontSize: '10px', py: '3px', px: '6px' } }}>
                                  <TableCell sx={{ color: 'var(--color-text-muted)' }}>{idx + 1}</TableCell>
                                  <TableCell sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.product_name}</TableCell>
                                  <TableCell sx={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{p.brand}</TableCell>
                                  <TableCell sx={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{p.category}</TableCell>
                                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'var(--color-danger)', fontWeight: 600 }}>
                                    {p.lost_sales_qty.toLocaleString()}
                                  </TableCell>
                                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'var(--color-danger)' }}>
                                    {p.lost_sales_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                  </TableCell>
                                </TableRow>
                              ))}
                              {cat.top10.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={6} sx={{ textAlign: 'center', fontSize: '10px', color: 'var(--color-text-muted)', py: '12px' }}>
                                    No lost sales
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {/* Footer totals */}
                        <Box sx={{ px: '12px', py: '8px', borderTop: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontSize: '10px', fontWeight: 600 }}>Top 10 Total</Typography>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: 'var(--color-danger)' }}>
                                {cat.sumQty.toLocaleString()} units
                              </Typography>
                              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: 'var(--color-danger)' }}>
                                {fmtCurrency(cat.sumValue)}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>Impact within {tab.label}</Typography>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                              <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                {cat.pctQty}% qty
                              </Typography>
                              <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                                {cat.pctValue}% value
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {/* Detail Table */}
          {sortedData.length > 0 ? (
            <TableContainer component={Paper} sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortHeader field="sku" label="SKU" width={90} />
                    <TableCell sx={{ fontSize: '11px', fontWeight: 700, py: '6px', px: '8px', minWidth: 130 }}>Product</TableCell>
                    <SortHeader field="store_name" label="Store" width={110} />
                    <SortHeader field="avg_daily_sales" label="Avg Daily" align="right" width={70} />
                    <TableCell align="right" sx={{ fontSize: '11px', fontWeight: 700, py: '6px', px: '8px', width: 56, whiteSpace: 'nowrap' }}>Days</TableCell>
                    <SortHeader field="days_oos" label="OOS" align="right" width={50} />
                    <SortHeader field="actual_sales_qty" label="Actual Qty" align="right" width={76} />
                    <SortHeader field="lost_sales_qty" label="Lost Qty" align="right" width={70} />
                    <SortHeader field="lost_sales_value" label="Lost Value" align="right" width={90} />
                    <SortHeader field="service_level_pct" label="Service %" align="right" width={80} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((row: any, idx: number) => (
                    <TableRow key={`${row.product_id}-${row.store_id}`} hover sx={{ '& .MuiTableCell-body': { fontSize: '11px', py: '4px', px: '8px' } }}>
                      <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{row.sku}</TableCell>
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.product_name}</TableCell>
                      <TableCell sx={{ color: 'var(--color-text-muted)' }}>{row.store_name}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.avg_daily_sales != null ? Math.round(row.avg_daily_sales) : ''}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.days_in_period}</TableCell>
                      <TableCell align="right">
                        {row.days_oos > 0 ? (
                          <Typography component="span" sx={{ color: 'var(--color-danger)', fontWeight: 600, fontSize: '11px' }}>{row.days_oos}</Typography>
                        ) : (
                          <Typography component="span" sx={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>0</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.actual_sales_qty.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {row.lost_sales_qty > 0 ? (
                          <Typography component="span" sx={{ color: 'var(--color-danger)', fontWeight: 600, fontSize: '11px' }}>{row.lost_sales_qty.toLocaleString()}</Typography>
                        ) : (
                          <Typography component="span" sx={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>0</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {row.lost_sales_value > 0 ? (
                          <Typography component="span" sx={{ color: 'var(--color-danger)', fontSize: '11px' }}>{row.lost_sales_value.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                        ) : (
                          <Typography component="span" sx={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>0</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            padding: '1px 6px',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: getServiceLevelColor(row.service_level_pct),
                            backgroundColor: getServiceLevelBg(row.service_level_pct),
                          }}
                        >
                          {row.service_level_pct.toFixed(1)}%
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No data available for the selected filters.</Alert>
          )}
        </>
      )}

      {!data && !loading && !error && (
        <Box sx={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: '48px 20px', textAlign: 'center', background: 'var(--color-surface)' }}>
          <Typography sx={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--color-text-secondary)', mb: 0.5 }}>
            No Data Available
          </Typography>
          <Typography sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Select a date range and click "Load" to view service level metrics per SKU x Store.
            Data will appear here once inventory and sales records are populated.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ServiceLevelKPI;
